import { Alert, Anchor, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconFileTypePdf, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { InventoryDepreciationModal } from 'src/Components/Inventory/InventoryDepreciationModal';
import { InventoryItemTable } from 'src/Components/Inventory/InventoryItemTable';
import { formatEuros } from 'src/Components/Inventory/inventoryLabels';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import {
  openInventoryPdf,
  useRestoreInventoryItem,
} from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import {
  trackInventoryExported,
  trackInventoryItemDeleted,
  trackInventoryViewed,
} from 'src/Services/Mixpanel/events';

import styles from './InventoryPage.module.scss';

const UNDO_DELAY_MS = 10_000;

interface DeletedState {
  deletedItem?: { id: string; name: string };
}

/** The inventory of the house. Values are only shown to managers. */
const InventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const assetId = useCurrentAssetId();
  const { data, isLoading, isError } = useFetchInventory();
  const { mutate: restore } = useRestoreInventoryItem();
  const [deleted, setDeleted] = useState<{ id: string; name: string }>();
  const [exporting, setExporting] = useState(false);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const undoTimer = useRef<number>();

  useEffect(() => {
    if (data)
      trackInventoryViewed({ item_count: data.items.length, is_manager: data.valuesVisible });
    // Once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  // The item drawer comes back here after a deletion: « Annuler » stays 10 seconds.
  const deletedFromState = (location.state as DeletedState | null)?.deletedItem;
  useEffect(() => {
    if (!deletedFromState) return;
    setDeleted(deletedFromState);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedFromState?.id]);

  useEffect(() => {
    if (!deleted) return;
    undoTimer.current = window.setTimeout(() => setDeleted(undefined), UNDO_DELAY_MS);
    return () => window.clearTimeout(undoTimer.current);
  }, [deleted]);

  const roomCount = useMemo(
    () => new Set((data?.items ?? []).map(item => item.room?.id ?? null)).size,
    [data]
  );

  const undo = () => {
    if (!deleted) return;
    window.clearTimeout(undoTimer.current);
    restore(deleted.id, {
      onSuccess: item => {
        trackInventoryItemDeleted({ undone: true });
        navigate(item.id);
      },
      onError: () => {
        notifications.show({
          color: 'red',
          message: t('inventory.undoError', "L'objet n'a pas pu être remis."),
        });
      },
    });
    setDeleted(undefined);
  };

  const exportPdf = async () => {
    if (!assetId) return;
    setExporting(true);
    try {
      const pdf = await openInventoryPdf(
        async () => (await inventoryApi.inventoryControllerExportAll(assetId)).data
      );
      trackInventoryExported({
        item_count: data?.items.length ?? 0,
        value_to_complete_count: pdf.valueToCompleteCount,
        scope: 'house',
      });
      notifications.show({
        color: 'teal',
        message: t(
          'inventory.export.done',
          'Le PDF est prêt. Une action « Envoyer l’inventaire à l’assurance » vous le rappellera dans un an.'
        ),
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.export.error', "Le PDF n'a pas pu être préparé. Réessayez."),
      });
    } finally {
      setExporting(false);
    }
  };

  const hasItems = (data?.items.length ?? 0) > 0;

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('inventory.title', 'Inventaire')}
        rightElement={
          <div className={styles.topActions}>
            {(data?.exitedCount ?? 0) > 0 && (
              <Button
                variant="outline"
                leftSection={<IconArchive size={20} />}
                onClick={() => navigate('exited')}
              >
                {t('inventory.exited.link', 'Objets sortis ({{count}})', {
                  count: data?.exitedCount ?? 0,
                })}
              </Button>
            )}
            {data?.valuesVisible && hasItems && (
              <Button
                variant="outline"
                leftSection={<IconFileTypePdf size={20} />}
                loading={exporting}
                onClick={exportPdf}
              >
                {t('inventory.export.cta', 'PDF pour l’assurance')}
              </Button>
            )}
            <Button onClick={() => navigate('new')} leftSection={<IconPlus size={20} />}>
              {t('inventory.add.cta', 'Ajouter un objet')}
            </Button>
          </div>
        }
      />

      {deleted && (
        <Alert color="gray" className={styles.banner}>
          <div className={styles.bannerContent}>
            <span>{t('inventory.deleted', '{{name}} supprimé', { name: deleted.name })}</span>
            <Button variant="outline" onClick={undo}>
              {t('common.cancel', 'Annuler')}
            </Button>
          </div>
        </Alert>
      )}

      {data?.valuesVisible && hasItems && (
        <div className={styles.summary}>
          {data.totalCurrentValue != null && (
            <span>
              {t('inventory.summary.worthLine', 'Vaut aujourd’hui, à peu près {{value}}', {
                value: formatEuros(data.totalCurrentValue),
              })}
              {' · '}
              {t('inventory.summary.replacement', '{{value}} à neuf', {
                value: formatEuros(data.totalReplacementValue ?? 0),
              })}
              {' · '}
            </span>
          )}
          <span>
            {t('inventory.summary.count', '{{count}} objets dans {{rooms}} pièces', {
              count: data.items.length,
              rooms: roomCount,
            })}
            {' · '}
          </span>
          <span>
            {t('inventory.settings.rule', 'Usure : {{rate}} % par an, au plus {{max}} %.', {
              rate: data.settings.depreciationRatePercent,
              max: data.settings.depreciationMaxPercent,
            })}{' '}
            <Anchor component="button" type="button" onClick={() => setSettingsOpened(true)}>
              {t('inventory.settings.change', 'Changer')}
            </Anchor>
          </span>
        </div>
      )}

      {data?.valuesVisible && data.valueToCompleteCount > 0 && (
        <Alert color="orange" className={styles.banner}>
          {t('inventory.summary.toComplete', {
            count: data.valueToCompleteCount,
            defaultValue:
              '{{count}} objets sans valeur actuelle : il manque la valeur à neuf ou l’année d’achat.',
          })}
        </Alert>
      )}

      <div className={styles.cardContainer}>
        <InventoryItemTable
          items={data?.items}
          fetching={isLoading}
          valuesVisible={data?.valuesVisible}
          noRecordsText={
            isError
              ? t('inventory.error', "Impossible d'afficher l'inventaire.")
              : t('inventory.empty', 'Aucun objet pour le moment')
          }
          onItemClick={item => navigate(item.id)}
        />
      </div>

      {data && (
        <InventoryDepreciationModal
          opened={settingsOpened}
          onClose={() => setSettingsOpened(false)}
          rate={data.settings.depreciationRatePercent}
          max={data.settings.depreciationMaxPercent}
        />
      )}
      <Outlet />
    </div>
  );
};

export default InventoryPage;
